import React, { useContext, useEffect, useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { LendingContext, WizardContext } from 'context';
import { useNavService } from 'hooks';
import { routes } from 'router';
import { FloatingInput } from 'components';
import { getMonthlyRepaymentAmount, getValueOfLoanTermSelected, TealiumService } from 'services';
import {
  errorMessages,
  applicationDefaults,
  lendingOnboardingSteps,
  htmlFormat,
  masks,
  parseCurrency,
  validate,
} from 'utils';
import { formatCurrencyAmount } from 'helpers';
import './request.scss';
import { DropdownIcon, TickedCheckbox, UntickedCheckbox } from 'assets';

export const Request = () => {
  const { setNext, setNextEnabled } = useContext(WizardContext);
  const { application, updateApplication } = useContext(LendingContext);
  const navigate = useNavService();
  const formikRef = useRef(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    TealiumService.onBorrowDetailsStart();
    const timer = setTimeout(() => {
      if (formikRef.current) {
        const { rate, incomplete } = calculateFormStats(formikRef.current.values);
        TealiumService.onBorrowDetailsTimedView(rate, incomplete);
      }
    }, 120000);
    return () => clearTimeout(timer);
  }, []);

  function calculateFormStats(values) {
    const fieldLabels = {
      loanAmount: 'loan amount',
      loanTerm: 'loan term',
      loanUsage: 'loan usage',
      incomeSource: 'income source',
      incomeFrequency: 'income frequency',
      grossIncome: 'gross income',
      netIncome: 'net income',
      estimatedExpenses: 'estimated expenses',
    };
    const fields = Object.keys(fieldLabels);
    const incomplete = fields.filter((f) => !values[f]).map((f) => fieldLabels[f]);
    const rate = `${Math.round(((fields.length - incomplete.length) / fields.length) * 100)}%`;
    return { rate, incomplete: incomplete.join(', ') };
  }

  function isFormCompleted(values) {
    return (
      !validate.lang.isBlank(values.loanTerm) &&
      !validate.lang.isBlank(values.loanUsage) &&
      !validate.lang.isBlank(values.incomeSource) &&
      !validate.lang.isBlank(values.incomeFrequency) &&
      !validate.lang.isBlank(values.grossIncome) &&
      !validate.lang.isBlank(values.netIncome) &&
      !validate.lang.isBlank(values.estimatedExpenses) &&
      validate.number.maybeValid(parseCurrency(values.loanAmount).toString())
    );
  }

  function getInitialValues() {
    const request = application?.request;
    const loanTerm =
      request && applicationDefaults.loanTerms.find((obj) => obj.value === request.loanTerm);
    return {
      loanAmount: request?.loanAmount || '',
      loanTerm: loanTerm?.label || '',
      loanUsage: request?.loanUsage || '',
      incomeSource: request?.incomeSource || '',
      incomeFrequency: request?.incomeFrequency || '',
      grossIncome: request?.grossIncome || '',
      netIncome: request?.netIncome || '',
      estimatedExpenses: request?.estimatedExpenses || '',
    };
  }

  function validateForm({
    loanAmount,
    loanTerm,
    loanUsage,
    incomeSource,
    incomeFrequency,
    grossIncome,
    netIncome,
    estimatedExpenses,
  }) {
    const errors = {};

    if (!loanAmount || !validate.number.loanAmount(parseCurrency(loanAmount))) {
      errors.loanAmount = errorMessages.general.amount;
    }

    if (!loanTerm || !loanUsage) {
      errors.loan = errorMessages.general.loan;
    }

    if (!incomeSource || !incomeFrequency) {
      errors.income = errorMessages.general.income;
    }

    if (!grossIncome || grossIncome <= 0) {
      errors.grossIncome = errorMessages.general.grossIncome;
    }

    if (!netIncome || netIncome <= 0) {
      errors.netIncome = errorMessages.general.netIncome;
    } else if (grossIncome && parseCurrency(netIncome) >= parseCurrency(grossIncome)) {
      errors.netIncome = errorMessages.general.netIncomeExceedsGrossIncome;
    }

    if (!estimatedExpenses || estimatedExpenses <= 0) {
      errors.estimatedExpenses = errorMessages.general.estimatedExpenses;
    }

    return errors;
  }

  function save(values) {
    values.loanTerm = getValueOfLoanTermSelected(values);
    values.loanAmount = parseCurrency(values.loanAmount);
    values.grossIncome = parseCurrency(values.grossIncome);
    values.netIncome = parseCurrency(values.netIncome);
    values.estimatedExpenses = parseCurrency(values.estimatedExpenses);

    TealiumService.onBorrowDetailsEnd();

    updateApplication(lendingOnboardingSteps.request, values);
    navigate.to(routes.personalDetails);
  }

  return (
    <section className='request-container'>
      <article className='request-panel'>
        <header>
          <h2>How much would you like to borrow?</h2>
          <p>
            Please indicate how much you would like to borrow so we can get estimates from our
            lenders
          </p>
        </header>
        <section>
          <Formik
            innerRef={(formik) => {
              if (formik) {
                setNext(formik.submitForm);
                formikRef.current = formik;
              }
            }}
            initialValues={getInitialValues() || {}}
            initialErrors={isFormCompleted({}) ? {} : { incomplete: 'true' }}
            validate={validateForm}
            validateOnChange
            validateOnBlur={false}
            validateOnMount
            enableReinitialize
            onSubmit={save}
          >
            {({ values, setFieldValue, errors, isSubmitting, validateForm }) => {
              const canGoNext =
                !isSubmitting && !validate.hasErrors(errors) && isFormCompleted(values) && privacyAccepted;
              setTimeout(() => {
                setNextEnabled(canGoNext);
              }, 1);

              return (
                <Form>
                  <Field
                    name='loanAmount'
                    type='text'
                    placeholder='Loan amount (R500 - R350,000)'
                    component={FloatingInput}
                    fieldType='masked'
                    mask={(e) => masks.currencyAmount(e)}
                    className='top-field'
                    autoFocus
                  />
                  <Field
                    name='loanUsage'
                    type='text'
                    placeholder='Purpose of the loan'
                    component={FloatingInput}
                    items={applicationDefaults.loanUsage}
                    getItemDisplayValue={(item) => htmlFormat.decode(item.label)}
                    filterItemsWithInputValue
                    fieldType='select'
                    className='top-field'
                    dropdownIcon={DropdownIcon}
                  />
                  <Field
                    name='loanTerm'
                    type='text'
                    placeholder='Loan Term'
                    component={FloatingInput}
                    items={applicationDefaults.loanTerms}
                    getItemDisplayValue={(item) => htmlFormat.decode(item.label)}
                    filterItemsWithInputValue
                    fieldType='select'
                    className='top-field'
                    dropdownIcon={DropdownIcon}
                  />

                  <Field
                    name='incomeSource'
                    type='text'
                    placeholder='Income source'
                    component={FloatingInput}
                    items={applicationDefaults.incomeSource}
                    getItemDisplayValue={(item) => htmlFormat.decode(item.label)}
                    filterItemsWithInputValue
                    fieldType='select'
                    className='top-field'
                    dropdownIcon={DropdownIcon}
                  />
                  <Field
                    name='incomeFrequency'
                    type='text'
                    placeholder='Income frequency'
                    component={FloatingInput}
                    items={applicationDefaults.incomeFrequency}
                    getItemDisplayValue={(item) => htmlFormat.decode(item.label)}
                    filterItemsWithInputValue
                    fieldType='select'
                    className='top-field'
                    dropdownIcon={DropdownIcon}
                  />
                  <Field
                    name='grossIncome'
                    type='text'
                    placeholder='Gross monthly income (before deductions)'
                    component={FloatingInput}
                    fieldType='masked'
                    mask={(e) => masks.currencyAmount(e)}
                    className='top-field'
                  />
                  <Field
                    name='netIncome'
                    type='text'
                    placeholder='Net monthly income (after deductions)'
                    component={FloatingInput}
                    fieldType='masked'
                    mask={(e) => masks.currencyAmount(e)}
                    className='top-field'
                  />
                  <Field
                    name='estimatedExpenses'
                    type='text'
                    placeholder='Estimated monthly expenses'
                    component={FloatingInput}
                    fieldType='masked'
                    mask={(e) => masks.currencyAmount(e)}
                    className='top-field'
                  />

                  {!validate.hasErrors(errors) && isFormCompleted(values) && (
                    <section className='monthly-repayment-block'>
                      <h3 className='monthly-repayment-title'>Approximate monthly repayment</h3>
                      <p className='monthly-repayment-amount'>
                        R {formatCurrencyAmount(getMonthlyRepaymentAmount(values))}*
                      </p>
                      <p className='monthly-repayment-tcs'>
                        *For display purposes, monthly repayments are calculated using a 23% per
                        annum interest rate.
                        <br />
                        Actual pricing may differ depending on the risk assessment for the
                        individual lender.
                      </p>
                    </section>
                  )}
                </Form>
              );
            }}
          </Formik>
        </section>
      </article>
      <div className='privacy-notice-checkbox' onClick={() => setPrivacyAccepted((v) => !v)}>
        <img src={privacyAccepted ? TickedCheckbox : UntickedCheckbox} alt='toggle-icon' />
        <span className='privacy-notice-checkbox__text'>
          Your personal information will be shared with participating lenders to generate personalised
          quotes. View our{' '}
          <a
            href='https://www.vodacom.co.za/vodacom/privacy-policy/vodalend-privacy-policy'
            target='_blank'
            rel='noopener noreferrer'
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Statement
          </a>
        </span>
      </div>
    </section>
  );
};
